import moment from 'moment';
import { militaryTimeToHourAndMinutes, NUM_DAYS_OF_WEEK } from '@/utils/date';
import { OpenHoursDetails } from '@/types';

export interface BusinessHours {
  endDay: number;
  endHour: number;
  endMinute: number;
  startDay: number;
  startHour: number;
  startMinute: number;
}

/**
 * Converts a Yelp {@link OpenHoursDetails} entry to the {@link BusinessHours} format used by
 * the hours dropdown.
 *
 * Yelp uses 0 = Monday; JS `Date.getDay()` uses 0 = Sunday.
 * Overnight entries are split so {@link BusinessHours.endDay} is the following day.
 */
export const transformOpenHoursDetails = (
  entry: OpenHoursDetails,
): BusinessHours => {
  const [startHour, startMinute] = militaryTimeToHourAndMinutes(entry.start);
  const [endHour, endMinute] = militaryTimeToHourAndMinutes(entry.end);
  const startDay = (entry.day + 1) % NUM_DAYS_OF_WEEK;

  return {
    endDay: entry.is_overnight ? (startDay + 1) % NUM_DAYS_OF_WEEK : startDay,
    endHour,
    endMinute,
    startDay,
    startHour,
    startMinute,
  };
};

/**
 * Groups an array of business hours entries into a map keyed by {@link BusinessHours.startDay}.
 *
 * @param hours - Flat list of business hour slots.
 * @returns A record mapping each day index (0 = Sunday … 6 = Saturday) to its slots.
 */
export const buildDayToHoursMap = (hours: BusinessHours[]) => {
  return hours.reduce<Record<number, BusinessHours[]>>((result, entry) => {
    if (!result[entry.startDay]) {
      result[entry.startDay] = [];
    }
    result[entry.startDay].push(entry);
    return result;
  }, {});
};

/**
 * Builds an ordered array of day indices starting from the given day and
 * cycling through the full week.
 *
 * @param currentDayOfWeek - JS `Date.getDay()` index (0 = Sunday … 6 = Saturday).
 * @returns An array of 7 day indices, e.g. Friday (5) yields `[5, 6, 0, 1, 2, 3, 4]`.
 */
export const buildOrderedDays = (currentDayOfWeek: number): number[] => {
  return Array.from(
    { length: NUM_DAYS_OF_WEEK },
    (_, i) => (currentDayOfWeek + i) % NUM_DAYS_OF_WEEK,
  );
};

/**
 * Formats a 24-hour time value into a compact 12-hour string.
 * Minutes are omitted when zero (e.g. `7am`), otherwise included (e.g. `7:30am`).
 *
 * @param hour   - Hour in 24-hour format (0–23).
 * @param minute - Minute (0–59).
 * @returns Formatted time string, e.g. `"7am"`, `"11:30pm"`.
 */
export const formatTime = (hour: number, minute: number): string =>
  moment({ hour, minute }).format(minute === 0 ? 'ha' : 'h:mma');

/**
 * Formats a business hours slot as a start–end range string.
 *
 * @param entry     - The business hours slot to format.
 * @param separator - Character(s) placed between start and end times. Defaults to an em-dash (`—`).
 * @returns Formatted range, e.g. `"7am—10pm"` or `"7am — 10pm"`.
 */
export const formatHoursRange = (
  entry: BusinessHours,
  separator = '—',
): string => {
  const start = formatTime(entry.startHour, entry.startMinute);
  const end = formatTime(entry.endHour, entry.endMinute);
  return [start, end].join(separator);
};

/**
 * Finds the currently active or next upcoming business hours slot.
 *
 * Search order:
 * 1. Overnight carry-over from yesterday (e.g. 10pm–2am still active at 1am).
 * 2. A slot on the current day that is active now or opens later today.
 * 3. The earliest slot on subsequent days, wrapping around the week.
 *
 * @param dayToHoursMap   - Map of day index to business hours slots (see {@link buildDayToHoursMap}).
 * @param currentDayOfWeek - JS `Date.getDay()` index (0–6).
 * @param currentHour     - Current hour (0–23).
 * @param currentMinute   - Current minute (0–59).
 * @returns The matching slot, or `null` if no hours exist at all.
 */
export const findNextOpenSlot = (
  dayToHoursMap: Record<number, BusinessHours[]>,
  currentDayOfWeek: number,
  currentHour: number,
  currentMinute: number,
): BusinessHours | null => {
  const now = currentHour * 60 + currentMinute;

  // Check all overnight slots from yesterday that may still be active
  const yesterday =
    (currentDayOfWeek - 1 + NUM_DAYS_OF_WEEK) % NUM_DAYS_OF_WEEK;
  for (const slot of dayToHoursMap[yesterday] ?? []) {
    if (
      slot.endDay === currentDayOfWeek &&
      slot.endHour * 60 + slot.endMinute > now
    ) {
      return slot;
    }
  }

  // Walk forward through the week starting from today
  for (let i = 0; i < NUM_DAYS_OF_WEEK; i++) {
    const day = (currentDayOfWeek + i) % NUM_DAYS_OF_WEEK;
    for (const slot of dayToHoursMap[day] ?? []) {
      if (i > 0) {
        return slot;
      } // Any slot on a future day

      // For today: skip slots that have already ended (strict > to match isRestaurantOpen)
      const endHour =
        slot.endDay !== slot.startDay ? slot.endHour + 24 : slot.endHour;
      if (endHour * 60 + slot.endMinute > now) {
        return slot;
      }
    }
  }

  return null;
};

/**
 * Determines whether the given business hours slot covers the current time.
 * Correctly handles overnight slots that span across midnight into the next day.
 *
 * @param slot            - The business hours slot to check.
 * @param currentDayOfWeek - JS `Date.getDay()` index (0–6).
 * @param currentHour     - Current hour (0–23).
 * @param currentMinute   - Current minute (0–59).
 * @returns `true` if the current time falls within the slot's range (inclusive).
 */
export const isRestaurantOpen = (
  slot: BusinessHours,
  currentDayOfWeek: number,
  currentHour: number,
  currentMinute: number,
): boolean => {
  if (slot.startDay === currentDayOfWeek) {
    const endHour =
      (slot.endDay - slot.startDay + NUM_DAYS_OF_WEEK) % NUM_DAYS_OF_WEEK === 1
        ? 24 + slot.endHour
        : slot.endHour;

    return (
      (slot.startHour < currentHour ||
        (slot.startHour === currentHour &&
          slot.startMinute <= currentMinute)) &&
      (endHour > currentHour ||
        (endHour === currentHour && slot.endMinute > currentMinute))
    );
  }

  if (slot.endDay === currentDayOfWeek) {
    return (
      slot.endHour > currentHour ||
      (slot.endHour === currentHour && slot.endMinute > currentMinute)
    );
  }

  return false;
};
